import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, HStack, VStack, Select, Input, Text, IconButton, useToast, Box,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { useRecurringEarners, useRecurringEarnersSave, RecurringEarner } from '../hooks/useRecurringEarners';
import useUsers from '../hooks/useUsers';

// dart (2026-07-14) — #25: edit WHO earns recurring commission on a subscription (identity + %),
// going forward. Already-accrued months are immutable (idempotent accrual); the new set applies to
// future months only. Replaces the subscription's earner rows via POST RecurringRevenue/{id}/earners.
interface Props {
  recurringId?: string;
  recurringName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const EditRecurringEarnersModal: React.FC<Props> = ({
  recurringId, recurringName, isOpen, onClose,
}) => {
  const { data: fetched } = useRecurringEarners(isOpen ? recurringId : undefined);
  const { data: users } = useUsers();
  const save = useRecurringEarnersSave();
  const toast = useToast();
  const [rows, setRows] = useState<RecurringEarner[]>([]);

  useEffect(() => { if (isOpen) setRows(fetched ? [...fetched] : []); }, [isOpen, fetched]);

  const setRow = (i: number, patch: Partial<RecurringEarner>) => setRows(
    (prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
  );
  const addRow = () => setRows((prev) => [...prev, { userId: '', percent: 0, recurringRevenueItemId: null }]);
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    const clean = rows.filter((r) => r.userId && r.percent > 0);
    if (clean.some((r) => r.percent > 100)) {
      toast({
        title: 'A percentage can’t exceed 100%.', status: 'error', duration: 5000, isClosable: true,
      });
      return;
    }
    try {
      await save.mutateAsync({ id: recurringId as string, earners: clean });
      toast({
        title: 'Commission split updated',
        description: 'Applies to future months — already-accrued months are unchanged.',
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      onClose();
    } catch (e: any) {
      toast({
        title: 'Update failed',
        description: e?.response?.data ?? 'The commission split could not be saved.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit commission split</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={3}>
            {recurringName ? `Subscription: ${recurringName}. ` : ''}
            Changes apply to
            {' '}
            <b>future months</b>
            {' '}
            only — already-accrued commissions stay as booked. Reverse a wrong past month from the
            commissions ledger.
          </Text>
          <VStack align="stretch" spacing={2}>
            {rows.length === 0 && (
              <Text fontSize="sm" color="gray.500">No earners — add one, or leave empty for no recurring commission.</Text>
            )}
            {rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <HStack key={i}>
                <Select
                  size="sm"
                  flex="1"
                  placeholder="Select earner…"
                  value={r.userId}
                  onChange={(e) => setRow(i, { userId: e.target.value })}
                  aria-label="earner"
                >
                  {(users?.index ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Select>
                <Input
                  size="sm"
                  type="number"
                  step="0.01"
                  min={0}
                  w="90px"
                  textAlign="right"
                  value={r.percent}
                  onChange={(e) => setRow(i, { percent: e.target.value === '' ? 0 : Number(e.target.value) })}
                  aria-label="percent"
                />
                <Text w="14px">%</Text>
                <IconButton size="xs" variant="ghost" aria-label="Remove earner" icon={<FiTrash2 />} onClick={() => removeRow(i)} />
              </HStack>
            ))}
          </VStack>
          <Box mt={3}>
            <Button size="xs" leftIcon={<FiPlus />} variant="outline" onClick={addRow}>Add earner</Button>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="purple" isLoading={save.isLoading} onClick={submit}>Save split</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

EditRecurringEarnersModal.defaultProps = { recurringId: undefined, recurringName: undefined };

export default EditRecurringEarnersModal;
